import { useEffect } from 'react';
import { useStoreSettings } from '../../../hooks/useStoreSettings';
import { useLocation } from 'react-router-dom';

const DocumentHead = () => {
    const { storeSettings, loading } = useStoreSettings();
    const location = useLocation();

    useEffect(() => {
        // console.log('DocumentHead - Store settings:', { storeSettings, loading });

        if (!loading && storeSettings) {
            const storeName = storeSettings.name?.trim() || 'Online Store';
            const storeDescription = storeSettings.description?.trim();
            const currentUrl = window.location.href;

            // Update document title
            // console.log('Updating document title to:', storeName);
            document.title = storeName;

            // Update meta description
            // console.log('Updating meta description to:', storeDescription);
            updateMetaTag('name', 'description', storeDescription);

            // Update meta keywords (basic ones + store name)
            const keywords = `${storeName}, ecommerce, online store, products, shopping`;
            updateMetaTag('name', 'keywords', keywords);

            // Update meta author
            updateMetaTag('name', 'author', storeName);

            // Update Open Graph tags
            updateMetaTag('property', 'og:title', storeName);
            updateMetaTag('property', 'og:description', storeDescription);
            updateMetaTag('property', 'og:url', currentUrl);
            updateMetaTag('property', 'og:site_name', storeName);

            // Update Twitter Card tags
            updateMetaTag('name', 'twitter:title', storeName);
            updateMetaTag('name', 'twitter:description', storeDescription);

            // Update Open Graph image and Twitter image if logo is available
            if (storeSettings.logoUrl && storeSettings.logoUrl.trim()) {
                // console.log('Updating OG image to:', storeSettings.logoUrl);
                updateMetaTag('property', 'og:image', storeSettings.logoUrl);
                updateMetaTag('property', 'og:image:alt', `${storeName} Logo`);
                updateMetaTag('property', 'og:image:type', 'image/png');
                updateMetaTag('property', 'og:image:width', '512');
                updateMetaTag('property', 'og:image:height', '512');

                updateMetaTag('name', 'twitter:image', storeSettings.logoUrl);
                updateMetaTag('name', 'twitter:image:alt', `${storeName} Logo`);
                updateMetaTag('name', 'twitter:card', 'summary_large_image');

                // Also update favicon
                updateFavicon(storeSettings.logoUrl);
            } else {
                // console.log('No logo URL found in store settings - removing OG images');
                // Remove image tags to show text-only sharing
                removeMetaTag('property', 'og:image');
                removeMetaTag('property', 'og:image:alt');
                removeMetaTag('property', 'og:image:type');
                removeMetaTag('property', 'og:image:width');
                removeMetaTag('property', 'og:image:height');
                removeMetaTag('name', 'twitter:image');
                removeMetaTag('name', 'twitter:image:alt');
                updateMetaTag('name', 'twitter:card', 'summary');
            }
        }
    }, [storeSettings, loading, location.pathname]);

    // Helper function to update or create meta tags
    const updateMetaTag = (attribute, attributeValue, content) => {
        let metaTag = document.querySelector(`meta[${attribute}="${attributeValue}"]`);
        if (metaTag) {
            metaTag.setAttribute('content', content);
        } else {
            metaTag = document.createElement('meta');
            metaTag.setAttribute(attribute, attributeValue);
            metaTag.setAttribute('content', content);
            document.head.appendChild(metaTag);
        }
    };

    // Helper function to remove meta tags
    const removeMetaTag = (attribute, attributeValue) => {
        const metaTag = document.querySelector(`meta[${attribute}="${attributeValue}"]`);
        if (metaTag) {
            metaTag.remove();
        }
    };

    // Helper function to update favicon
    const updateFavicon = (logoUrl) => {
        let favicon = document.querySelector('link[rel="icon"]');
        if (favicon) {
            favicon.href = logoUrl;
        } else {
            favicon = document.createElement('link');
            favicon.rel = 'icon';
            favicon.href = logoUrl;
            document.head.appendChild(favicon);
        }
    };

    // This component doesn't render anything visible
    return null;
};

export default DocumentHead;